import { useEffect, useState } from 'react';
import { Form } from 'antd';
import dayjs from 'dayjs';
import type { OemPartnerGroupDto } from '@evimo/client/models';
import { SlaContractStatus } from '@evimo/types/enums/sla-contract-status';
import type { PartnerOrgSelection } from '~/app/landing/sla/shared/types';
import { PartnersService } from '~/app/services/partners/partners.service';
import type { OemPartnerFormValues } from '@/app/landing/sla/oems/OemPartnerModal/types';
import { DEFAULT_OEM_RATES } from '@/app/landing/sla/oems/OemPartnerModal/utils';

type Args = {
  open: boolean;
  organizationId: string;
  editingRow: OemPartnerGroupDto | null;
  onSuccess: () => void;
};

export function useOemPartnerModal({
  open,
  organizationId,
  editingRow,
  onSuccess,
}: Args) {
  const [form] = Form.useForm<OemPartnerFormValues>();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPartner, setSelectedPartner] =
    useState<PartnerOrgSelection | null>(null);
  const [contractSnapshot, setContractSnapshot] =
    useState<OemPartnerFormValues | null>(null);

  const isEdit = editingRow != null;

  useEffect(() => {
    if (!open) {
      return;
    }
    setStep(0);
    setContractSnapshot(null);
    if (editingRow?.slaContract) {
      setSelectedPartner({
        id: editingRow.partnerOrgId,
        name: editingRow.partnerName,
      });
      form.setFieldsValue({
        validFrom: dayjs(editingRow.slaContract.validFrom),
        validTo: editingRow.slaContract.validTo
          ? dayjs(editingRow.slaContract.validTo)
          : null,
        status: editingRow.slaContract.status,
        rateEntries: editingRow.slaContract.rateEntries.map((entry) => ({
          windowValue: entry.windowValue,
          windowUnit: entry.windowUnit,
          costAmount: entry.costAmount,
          currency: entry.currency ?? 'EUR',
        })),
      });
    } else {
      form.resetFields();
      setSelectedPartner(null);
      form.setFieldsValue({
        validFrom: dayjs(),
        status: SlaContractStatus.ACTIVE,
        rateEntries: DEFAULT_OEM_RATES,
      });
    }
  }, [open, editingRow, form]);

  const handleSubmit = async () => {
    const values =
      !isEdit && contractSnapshot
        ? contractSnapshot
        : await form.validateFields();

    setSubmitting(true);
    try {
      const payload = {
        validFrom: values.validFrom.toDate(),
        validTo: values.validTo ? values.validTo.toDate() : null,
        status: values.status,
        rateEntries: values.rateEntries,
      };

      if (isEdit && editingRow) {
        await PartnersService.updateOem(
          organizationId,
          editingRow.relationshipId,
          payload,
        );
      } else {
        await PartnersService.createOem(organizationId, {
          ...payload,
          partnerOrgId: selectedPartner?.id,
          partnerName: selectedPartner?.id ? undefined : selectedPartner?.name,
        });
      }
      onSuccess();
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (step === 2) {
      try {
        const values = await form.validateFields();
        setContractSnapshot(values);
      } catch {
        return;
      }
    }
    setStep((current) => current + 1);
  };

  const canProceedFromPartnerStep = Boolean(
    selectedPartner?.name && !selectedPartner.alreadyLinked,
  );

  return {
    form,
    step,
    setStep,
    submitting,
    selectedPartner,
    setSelectedPartner,
    contractSnapshot,
    isEdit,
    handleSubmit,
    handleNext,
    canProceedFromPartnerStep,
  };
}
